import * as React from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  DialogActions,
  RadioGroup, Radio, FormControlLabel,
  Typography, DialogTitle, Grid, InputAdornment,
  Tabs, Tab, Box, Stack, Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {protectedResources} from "../../../authConfig";
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import * as yup from 'yup';
import { useFormik } from 'formik';
import {useTranslation} from "react-i18next";
import { LoadingButton } from "@mui/lab";
import CustomFormLabel from '../../../components/forms/theme-elements/CustomFormLabel';

// Map backend DN JSON to formik DN structure with per-year typology
const mapBackendDNToFormikDN = (response) => {
  // response is expected to be an array of { ReferenceYear, DNList: [ { ComputeType, DNValues: [ { ThesholdAmount, CampaignTypeValues: { Branding, Performance, Programmatic } } ] } ] }
  const dnYears = {};

  if (!Array.isArray(response) || response.length === 0) {
    return dnYears;
  }

  response.forEach((yearEntry) => {
    const yearKey = String(yearEntry.ReferenceYear);
    if (!dnYears[yearKey]) {
      dnYears[yearKey] = {
        typology: 'Scaglioni',
        values: [],
      };
    }

    (yearEntry.DNList || []).forEach((dn) => {
      dnYears[yearKey].typology = dn.ComputeType || dnYears[yearKey].typology || 'Scaglioni';

      (dn.DNValues || []).forEach((val) => {
        const amount = val.ThesholdAmount ?? null;
        const branding = val.CampaignTypeValues?.Branding ?? null;
        const performance = val.CampaignTypeValues?.Performance ?? null;
        const programmatic = val.CampaignTypeValues?.Programmatic ?? null;
        dnYears[yearKey].values.push({ amount, branding, performance, programmatic });
      });
    });
  });

  Object.keys(dnYears).forEach((yearKey) => {
    dnYears[yearKey].values.sort((a, b) => (a.amount || 0) - (b.amount || 0));
  });

  return dnYears;
};

// Helper: format a numeric value using Italian formatting (e.g. 10.000,50)
const formatItalianNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' &&  value.endsWith(",")) {
    return value;
  }
  let num = typeof value === 'number' ? value : parseItalianNumber(value);
  if (num === null || Number.isNaN(num)) {
    return typeof value === 'string' ? value : '';
  }
  return num.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// Helper: parse an Italian-formatted number string back to a JS number
const parseItalianNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  let str = String(value).trim();
  if (!str) return null;
  // Remove thousands separator and normalize decimal comma to dot
  str = str.replace(/\./g, '').replace(/,/g, '.');
  const num = Number(str);
  return Number.isNaN(num) ? null : num;
};

const AddAgencyDN = ({ open, close, agencyId, agencyName}) => {
  const { t } = useTranslation();
  const validationSchema = yup.object({

  });
  const validateDN = React.useCallback((values) => {
    const errors = {};
    const dnEntries = values?.DN || {};
    const dnErrors = {};

    Object.entries(dnEntries).forEach(([year, entry]) => {
      if (!entry || !Array.isArray(entry.values)) {
        return;
      }

      const yearErrors = {};
      const valueErrors = [];

      entry.values.forEach((threshold, index) => {
        const thresholdErrors = {};
        const amountValue = threshold?.amount;
        const amountNumber = parseItalianNumber(amountValue);
        const isAmountEmpty = amountValue === '' || amountValue === null || amountValue === undefined;

        if (entry.typology !== 'Flat' && (isAmountEmpty || Number.isNaN(amountNumber))) {
          thresholdErrors.amount = t('Required field');
        }

        ['programmatic', 'branding', 'performance'].forEach((fieldKey) => {
          const fieldValue = threshold?.[fieldKey];
          const parsedValue = parseItalianNumber(fieldValue);
          const isFieldEmpty = fieldValue === '' || fieldValue === null || fieldValue === undefined;

          if (isFieldEmpty || Number.isNaN(parsedValue)) {
            thresholdErrors[fieldKey] = t('Required field');
          }
        });

        if (Object.keys(thresholdErrors).length > 0) {
          valueErrors[index] = thresholdErrors;
        }
      });

      if (valueErrors.length > 0) {
        yearErrors.values = valueErrors;
      }

      if (Object.keys(yearErrors).length > 0) {
        dnErrors[year] = yearErrors;
      }
    });

    if (Object.keys(dnErrors).length > 0) {
      errors.DN = dnErrors;
    }

    return errors;
  }, [t]);
  const {execute: executeGet} = useFetchWithMsal({
    scopes: protectedResources.apiGetDNAgency.scopes.read,
  });
  const { execute: executeSubmit, isLoading: isLoadingSubmit } = useFetchWithMsal({
    scopes: protectedResources.agencyReferral.scopes.read,
  });

  const [years, setYears] = React.useState([]);
  const [activeYearIndex, setActiveYearIndex] = React.useState(0);
  const [lockedYears, setLockedYears] = React.useState([]);

  const formik = useFormik({
    initialValues: {
      AgencyName: '',
      AgencyId: null,
      DN: {},
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    validate: validateDN,
    validateOnMount: true,
    onSubmit: (values) => {
      const dnData = values.DN || {};
      const payload = Object.keys(dnData).map(year => {
        const yearEntry = dnData[year];
        const dnValues = (yearEntry.values || []).map(item => ({
          ThesholdAmount: parseItalianNumber(item.amount),
          CampaignTypeValues: {
            Branding: parseItalianNumber(item.branding),
            Performance: parseItalianNumber(item.performance),
            Programmatic: parseItalianNumber(item.programmatic),
          }
        }));

        return {
          ReferenceYear: parseInt(year, 10),
          DNList: [
            {
              ComputeType: yearEntry.typology,
              DNValues: dnValues,
            }
          ]
        };
      });

      console.log('Submitting agency DN payload:', payload);
      executeSubmit("POST", protectedResources.apiAddDNAgency.endpoint.replace('{agencyId}', agencyId), payload).then(() => {
        close();
      });
    },
  });

  const getThresholdFieldError = React.useCallback((year, idx, field) => {
    const fieldTouched = formik.touched?.DN?.[year]?.values?.[idx]?.[field];
    const fieldError = formik.errors?.DN?.[year]?.values?.[idx]?.[field];
    return fieldTouched ? fieldError : undefined;
  }, [formik.errors, formik.touched]);

  const markThresholdTouched = React.useCallback((year, idx, field) => {
    formik.setFieldTouched(`DN.${year}.values.${idx}.${field}`, true, false);
  }, [formik]);

  React.useEffect(() => {
    if (!open || !agencyId) return;
    executeGet("GET", protectedResources.apiGetDNAgency.endpoint.replace('{agencyId}', agencyId )).then((response) => {
      try {
        // If backend returns an empty array or falsy value, seed with a default DN row
        if (!response || (Array.isArray(response) && response.length === 0)) {
          const currentYear = String(new Date().getFullYear());
          const defaultDN = {
            [currentYear]: {
              typology: 'Scaglioni',
              values: [
                { amount: 10000, branding: 10, performance: 10, programmatic: 10 },
              ],
            },
          };

          formik.setFieldValue('DN', defaultDN, false);
          setYears([currentYear]);
          setActiveYearIndex(0);
          setLockedYears([]);
          return;
        }

        const mappedDN = mapBackendDNToFormikDN(response);
        formik.setFieldValue('DN', mappedDN, false);
        const yearKeys = Object.keys(mappedDN).sort();
        if (yearKeys.length > 0) {
          setYears(yearKeys);
          setActiveYearIndex(yearKeys.length - 1);
          setLockedYears(yearKeys);
        } else {
          const currentYear = String(new Date().getFullYear());
          setYears([currentYear]);
          formik.setFieldValue('DN', {
            [currentYear]: {
              typology: 'Scaglioni',
              values: [],
            },
          }, false);
          setActiveYearIndex(0);
          setLockedYears([]);
        }
      } catch (error) {
        console.error(error);
      }
    });
  },  [executeGet, open, agencyId]);

  const handleClose = () => {
    close();
  };

  const handleChangeYear = (event, newValue) => {
    setActiveYearIndex(newValue);
  };

  const addYear = () => {
    // Determine a new year key based on the maximum existing year, or current year if none
    const numericYears = years
      .map((y) => parseInt(y, 10))
      .filter((n) => !Number.isNaN(n));

    let newYear;
    if (numericYears.length > 0) {
      const maxYear = Math.max(...numericYears);
      newYear = maxYear + 1;
    } else {
      newYear = new Date().getFullYear();
    }

    const newYearKey = String(newYear);

    const currentDN = formik.values.DN || {};

    // If year already exists, just focus it
    if (currentDN[newYearKey]) {
      if (!years.includes(newYearKey)) {
        const updatedYears = [...years, newYearKey].sort();
        setYears(updatedYears);
        setActiveYearIndex(updatedYears.indexOf(newYearKey));
      } else {
        setActiveYearIndex(years.indexOf(newYearKey));
      }
      return;
    }

    // Copy from previous year (max numeric year) if available, otherwise use a default
    let newYearEntry;
    if (numericYears.length > 0) {
      const maxYear = Math.max(...numericYears);
      const prevYearKey = String(maxYear);
      const prevEntry = currentDN[prevYearKey];
      if (prevEntry) {
        newYearEntry = {
          typology: prevEntry.typology || 'Scaglioni',
          values: Array.isArray(prevEntry.values)
            ? prevEntry.values.map((v) => ({ ...v }))
            : [],
        };
      }
    }

    if (!newYearEntry) {
      newYearEntry = {
        typology: 'Scaglioni',
        values: [
          { amount: 10000, branding: 10, performance: 10, programmatic: 10 },
        ],
      };
    }

    const updatedDN = {
      ...currentDN,
      [newYearKey]: newYearEntry,
    };

    formik.setFieldValue('DN', updatedDN, false);
    const updatedYears = [...years, newYearKey].sort();
    setYears(updatedYears);
    setActiveYearIndex(updatedYears.indexOf(newYearKey));
  };

  const deleteActiveYear = () => {
    if (years.length === 0) return;
    const yearToDelete = years[activeYearIndex];
    if (!yearToDelete || lockedYears.includes(yearToDelete)) return;

    const currentDN = formik.values.DN || {};
    const { [yearToDelete]: _, ...restDN } = currentDN;

    const updatedYears = years.filter((y) => y !== yearToDelete);
    const updatedLockedYears = lockedYears.filter((y) => y !== yearToDelete);

    formik.setFieldValue('DN', restDN, false);
    setYears(updatedYears);
    setLockedYears(updatedLockedYears);

    if (updatedYears.length === 0) {
      setActiveYearIndex(0);
    } else if (activeYearIndex >= updatedYears.length) {
      setActiveYearIndex(updatedYears.length - 1);
    } else {
      setActiveYearIndex(activeYearIndex);
    }
  };

  const addThresholdForYear = (year) => {
    if (!year) return;
    const currentYearEntry = formik.values.DN?.[year] || { typology: 'Scaglioni', values: [] };
    const currentValues = currentYearEntry.values || [];

    let newItem;
    if (currentValues.length === 0) {
      // No existing soglie: seed sensible defaults
      newItem = { amount: '', branding: 10, performance: 10, programmatic: 0 };
    } else {
      // Copy values from the last existing soglia
      const last = currentValues[currentValues.length - 1];
      newItem = {
        amount: last.amount ?? '',
        branding: last.branding ?? '',
        performance: last.performance ?? '',
        programmatic: last.programmatic ?? '',
      };
    }

    const updatedValues = [...currentValues, newItem];
    formik.setFieldValue(`DN.${year}`, { ...currentYearEntry, values: updatedValues });
  };

  const removeThresholdForYear = (year, idx) => {
    if (!year) return;
    const currentYearEntry = formik.values.DN?.[year] || { typology: 'Scaglioni', values: [] };
    const currentValues = currentYearEntry.values || [];
    const updatedValues = currentValues.filter((_, i) => i !== idx);
    formik.setFieldValue(`DN.${year}`, { ...currentYearEntry, values: updatedValues });
  };

  const setThresholdField = (year, idx, field, value) => {
    if (!year) return;
    const currentYearEntry = formik.values.DN?.[year] || { typology: 'Scaglioni', values: [] };
    const currentValues = currentYearEntry.values || [];
    const updatedValues = currentValues.map((it, i) => i === idx ? { ...it, [field]: value } : it);
    formik.setFieldValue(`DN.${year}`, { ...currentYearEntry, values: updatedValues });
  };

  const setTypologyForYear = (year, value) => {
    if (!year) return;
    const currentYearEntry = formik.values.DN?.[year] || { typology: 'Scaglioni', values: [] };
    let updatedValues = Array.isArray(currentYearEntry.values) ? [...currentYearEntry.values] : [];

    // If typology is Flat, force all threshold amounts to 0
    // and ensure there is at most one threshold column
    if (value === 'Flat') {
      // Normalize to at most one item (keep first, drop others)
      if (updatedValues.length > 1) {
        updatedValues = [updatedValues[0]];
      }

      // If there is at least one item, set its amount to null; if not, leave as empty array
      updatedValues = updatedValues.map((v) => ({
        ...v,
        amount: 0,
      }));
    }

    formik.setFieldValue(`DN.${year}`, { ...currentYearEntry, typology: value, values: updatedValues });
  };

  const activeYear = years[activeYearIndex];
  const activeYearEntry = activeYear ? formik.values.DN?.[activeYear] : null;
  const activeYearValues = activeYearEntry?.values || [];
  const activeYearTypology = activeYearEntry?.typology || 'Scaglioni';
  const isActiveYearLocked = Boolean(activeYear && lockedYears.includes(activeYear));

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>{ agencyName} :   DN </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{t('Year')}</Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={activeYearIndex} onChange={handleChangeYear} sx={{ flexGrow: 1 }}>
                    {years.map((y) => (
                      <Tab key={y} label={y} />
                    ))}
                  </Tabs>
                  <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                    <Button size="small" variant="outlined" onClick={addYear}>
                      {t('Add year')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={deleteActiveYear}
                      disabled={years.length === 0 || isActiveYearLocked}
                    >
                      {t('Delete year')}
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              {activeYear && (
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">{t('Typology')} </Typography>
                    </Box>
                    <RadioGroup
                      row
                      value={activeYearTypology}
                      onChange={(e) => setTypologyForYear(activeYear, e.target.value)}
                    >
                      <FormControlLabel value="Scaglioni" control={<Radio />} label={t('Scaglioni')} />
                      {/*<FormControlLabel value="Scaglioni retroattivi" control={<Radio />} label={t('Scaglioni retroattivi')} />*/}
                      <FormControlLabel value="Flat" control={<Radio />} label={t('Flat')} />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      {activeYearTypology !== 'Flat' && (

                        <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        onClick={() => addThresholdForYear(activeYear)}
                        disabled={!activeYear}
                      >
                        {t('Add new bracket')}
                      </Button>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {activeYearTypology !== 'Flat' && <TableCell>{t('Bracket')}</TableCell>}
                            <TableCell>{t('Programmatic')}</TableCell>
                            <TableCell>{t('Branding')}</TableCell>
                            <TableCell>{t('Performance')}</TableCell>
                            {activeYearTypology !== 'Flat' && <TableCell width={80}></TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeYearValues.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={activeYearTypology !== 'Flat' ? 5 : 4} align="center">
                                {t('Nessuna soglia disponibile')}
                              </TableCell>
                            </TableRow>
                          ) : (
                            activeYearValues.map((thr, idx) => (
                              <TableRow key={`threshold-row-${idx}`}>
                                {activeYearTypology !== 'Flat' && (
                                  <TableCell>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      value={formatItalianNumber(thr.amount)}
                                      onChange={(e) => setThresholdField(activeYear, idx, 'amount', e.target.value)}
                                      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                                      label={t('Up to')}
                                      error={Boolean(getThresholdFieldError(activeYear, idx, 'amount'))}
                                      helperText={getThresholdFieldError(activeYear, idx, 'amount')}
                                      onBlur={() => {
                                        markThresholdTouched(activeYear, idx, 'amount');
                                      }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell align="right">
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={formatItalianNumber(thr.programmatic)}
                                    onChange={(e) => setThresholdField(activeYear, idx, 'programmatic', e.target.value)}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    error={Boolean(getThresholdFieldError(activeYear, idx, 'programmatic'))}
                                    helperText={getThresholdFieldError(activeYear, idx, 'programmatic')}
                                    onBlur={() => {
                                      markThresholdTouched(activeYear, idx, 'programmatic');
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={formatItalianNumber(thr.branding)}
                                    onChange={(e) => setThresholdField(activeYear, idx, 'branding', e.target.value)}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    error={Boolean(getThresholdFieldError(activeYear, idx, 'branding'))}
                                    helperText={getThresholdFieldError(activeYear, idx, 'branding')}
                                    onBlur={() => {
                                      markThresholdTouched(activeYear, idx, 'branding');
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={formatItalianNumber(thr.performance)}
                                    onChange={(e) => setThresholdField(activeYear, idx, 'performance', e.target.value)}
                                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    error={Boolean(getThresholdFieldError(activeYear, idx, 'performance'))}
                                    helperText={getThresholdFieldError(activeYear, idx, 'performance')}
                                    onBlur={() => {
                                      markThresholdTouched(activeYear, idx, 'performance');
                                    }}
                                  />
                                </TableCell>
                                {activeYearTypology !== 'Flat' && (
                                  <TableCell>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => removeThresholdForYear(activeYear, idx)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>

          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{ t('Cancel')}</Button>
            <LoadingButton
              type={"submit"}
              loading={isLoadingSubmit}
              loadingIndicator={'Loading..'}
              variant="contained"
              disabled={!formik.isValid}
            >
              <span>{t('Save')}</span>
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default AddAgencyDN;
